'use client';

import { type HTMLAttributes, useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { TransportPageContentSection } from '@/app/components/hotel-detail/TransportPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';
import DestinationWithoutAirportMessage from '@/app/components/hotel-detail/flight-train/DestinationWithoutAirportMessage';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import {
  isBelgiumTrainsSpecialFare,
  isNotHotelInBrussels,
} from '@/app/components/hotel-detail/flight-train/TransportCommon';
import TimeCheckoutDepartureMessage from '@/app/components/hotel-detail/flight-train/TimeCheckoutDepartureMessage';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import AirportInDifferentIslandMessage from '@/app/components/hotel-detail/flight-train/AirportInDifferentIslandMessage';
import useTransportsFetch from '@/app/components/hotel-detail/travel-api/transportsFetch';
import ChangeTransportPageLayout from '@/app/components/hotel-detail/changeTransport/ChangeTransportPageLayout';
import { TransportCard } from '@/app/components/hotel-detail/flight-train/TransportInformation';
import EvoTransportPageLayout from '@/app/components/hotel-detail/evo/transport/EvoTransportPageLayout';
import { ChangeTransportFilterProvider } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import { getTransportDistance } from '@/app/components/hotel-detail/common/transportDistance';
import { URLParameters } from '@/app/common/urlParameters';
import { PAGE_TYPE } from '@/app/common/pageType';
import { useCommonClient } from '@/app/common/commonClientProvider';
import type { ChangeFlightPage } from '@/app/common/starTrack/events/utils/starTrack.types';
import { createDpShownFlightsPayload } from '@/app/common/starTrack/events/dpShownFlights';
import { createChangeFlightPageShownPayload } from '@/app/common/starTrack/events/changeFlightPageShown';
import { createChooseFlightShownPayload } from '@/app/common/starTrack/events/chooseFlightShown';
import { v4 as uuidv4 } from 'uuid';
import { format, parse } from 'date-fns';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { isMealPlanInRooms } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import { getTransportIdParams } from './common/tracking';
import { trackEventError, trackVisitEvent } from './trackVisitEvents';
import { useFetchHotelPromotions } from './hooks/useFetchHotelPromotions';
import { useCreateAsyncPricing } from './PricingProvider';
import { useIsDesktop } from '../search/utils/useSsrMedia';
import { useAirlineSettings } from './hooks/useAirlineSettings';
import getSearchParams from '../search/common/getSearchParams';
import { useCuriosity } from './CuriosityProvider';

const BelgiumTrainsFareMessage = dynamic(
  () => import(/* webpackChunkName: "BelgiumTrainsFareMessage" */ './flight-train/BelgiumTrainsFareMessage'),
  { ssr: false }
);

const HotelNotInBrusselsMessage = dynamic(
  () => import(/* webpackChunkName: "HotelNotInBrusselsMessage" */ './flight-train/HotelNotInBrusselsMessage'),
  { ssr: false }
);

const ChangeTransportFiltersSortModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ChangeTransportFiltersSortModal" */ './changeTransport/ChangeTransportFiltersSortModal'
    ),
  { ssr: false }
);

type TransportSectionProps = {
  checkoutTo?: string;
} & HTMLAttributes<HTMLElement>;

export function TransportSection(props: TransportSectionProps) {
  const { checkoutTo, ...rest } = props;
  const { t } = useTranslation();
  const { offersData, isSoldout } = useOffersContext();
  const { internalHotelId, productDetailKey } = useInitialParams();
  const { staticData, commonData, searchInfoDetailData, searchInfoData, localization } = usePageWrapperData();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const {
    data: alternativeTransportsData,
    error: transportsFetchError,
    isFallbackTransport,
    isValidating: isValidatingTransports,
  } = useTransportsFetch();
  const { data: transportData } = useSelectedTransport();
  const { sendCuriosityFlightShownEvent } = useCuriosity();
  const { selectedMealPlan, selectedRoom } = useSelectedMealPlan();
  const { handleHotelChosen, starTrack } = useCommonClient();
  const urlSearchParams = useUrlSearchParams();
  const isDesktop = useIsDesktop(commonData.isDesktopDevice);
  const { currentFiltersStatus, page, visibleTransports, setVisibleTransports } = useCurrentFilterStatus();
  const hasInitialEventBeenSent = useRef(false);
  const lastTrackedFiltersSignatureRef = useRef<string | undefined>(undefined);
  const previousVisibleTransportsLength = useRef(0);
  const fallbackFlightClickId = useRef(uuidv4().slice(0, 32));
  const visitSentForFlightRef = useRef<string | undefined>(undefined);

  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const destination = searchParams[URLParameters.DESTINATION];
  // Origin of the change-flight redirection: the change-transport modal opens as an overlay without
  // navigating, so the current page type is where the user initiated it — hotel detail vs flight page.
  const changeFlightOriginPage: ChangeFlightPage =
    searchParams[URLParameters.PAGE_TYPE] === PAGE_TYPE.HOTEL_DETAIL ? 'Hotel Detail' : 'Flight Detail';

  const initialParams = useInitialParams();
  const { data: hotelPromotions } = useFetchHotelPromotions();
  const { isTrain, children: numberOfChildren, infants, nights, accommodationOnly } = urlSearchParams;
  const { data: asyncPricing } = useCreateAsyncPricing();
  useAirlineSettings(); // prefetch data
  // Temporally disabling branded fares prefetch
  // The branded fares for the selected transport should be cached and the response should be fast.
  // And there is an issue if the branded fares are called before alternative transport is finished, and this is happening
  // with this prefetch call if the user changes the room or meal plan, in this case we call alternative transports again
  // and the call to branded fares is done at the same time causing the issue.
  // The same if the user lands directly on the flights page.
  // useBrandedFares(
  //   transportData?.transports[0].eligibleBrandedFares ? transportData?.transports?.[0]?.id : undefined,
  //   0
  // ); // prefetch data

  const airportInDifferentIsland = isAirportInDifferentIsland(
    transportData?.transports || [],
    Number.isNaN(Number(destination)) ? hotelStaticData?.cityId : destination
  );
  const cityWithoutAirport = hotelStaticData?.withoutAirport;

  const distanceToHotelData = getTransportDistance(transportData);
  const transportIsSoldOut = transportData?.transports?.length === 0;
  const currentFiltersSignature = JSON.stringify(currentFiltersStatus || {});

  const sendDpShownFlightsEvent = useCallback(
    (transportsToTrack: AvailableTransport[] = visibleTransports) => {
      const defaultFlight = transportData?.transports?.[0];
      if (defaultFlight && transportsToTrack.length > 0 && !isTrain && selectedMealPlan) {
        const currentSorting = currentFiltersStatus?.sortOptions?.find((sort) => sort.selected)?.option || 'POPULAR';

        starTrack.dpShownFlights(
          createDpShownFlightsPayload({
            searchInfoData,
            initialParams,
            selectedMealPlan,
            availableTransports: transportsToTrack,
            defaultFlightId: defaultFlight.id,
            currentSorting,
            currency: localization.currency,
          })
        );
      }
    },
    [
      transportData,
      visibleTransports,
      isTrain,
      selectedMealPlan,
      currentFiltersStatus,
      starTrack,
      searchInfoData,
      initialParams,
      localization.currency,
    ]
  );

  useEffect(() => {
    if (isSoldout) {
      handleHotelChosen(internalHotelId.toString());
      trackEventError({
        errorType: 'ERROR_SEARCH_RESPONSE',
        initialParams,
        urlSearchParams,
        hotelStaticData,
        hotelPromotions,
        pricingId: asyncPricing?.pricingId || '',
        pageSection: 'hdp.chooseTransport',
        staticData,
        commonData,
        localization,
        searchInfoDetailData,
      });
    }
  }, [handleHotelChosen, internalHotelId, isSoldout]);

  // Update visible transports when initial data is loaded or filters change
  useEffect(() => {
    if (alternativeTransportsData && !isValidatingTransports) {
      const transportResponse = alternativeTransportsData as FullAvailableTransportResponse;
      const transports = transportResponse?.availableTransports || [];
      const responseFiltersSignature = JSON.stringify(transportResponse?.filters || {});
      setVisibleTransports(transports);

      if (
        hasInitialEventBeenSent.current &&
        page === 1 &&
        responseFiltersSignature === currentFiltersSignature &&
        lastTrackedFiltersSignatureRef.current !== undefined &&
        lastTrackedFiltersSignatureRef.current !== currentFiltersSignature
      ) {
        sendDpShownFlightsEvent(transports);
      }

      if (page === 1 && responseFiltersSignature === currentFiltersSignature) {
        lastTrackedFiltersSignatureRef.current = currentFiltersSignature;
      }

      // Reset the length tracker when data reloads (filter/sort change or initial load)
      previousVisibleTransportsLength.current = 0;
    }
  }, [
    alternativeTransportsData,
    currentFiltersSignature,
    isValidatingTransports,
    page,
    sendDpShownFlightsEvent,
    setVisibleTransports,
  ]);

  // Send DP_SHOWN_FLIGHTS event on initial load when visibleTransports is populated
  useEffect(() => {
    if (!hasInitialEventBeenSent.current && visibleTransports.length > 0 && transportData?.transports?.[0]) {
      sendDpShownFlightsEvent();
      hasInitialEventBeenSent.current = true;
      lastTrackedFiltersSignatureRef.current = currentFiltersSignature;
    }
  }, [currentFiltersSignature, visibleTransports, transportData, sendDpShownFlightsEvent]);

  useEffect(() => {
    const defaultFlight = transportData?.transports?.[0];
    if (defaultFlight) {
      const { inboundFlightId, outboundFlightId } = getTransportIdParams(defaultFlight);
      sendCuriosityFlightShownEvent({
        people_child: numberOfChildren,
        people_infant: infants,
        checkin_date: offersData?.checkin
          ? format(parse(offersData.checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
          : '', // Format YYYY-MM-DD
        checkout_date: offersData?.checkout
          ? format(parse(offersData.checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
          : '', // Format YYYY-MM-DD
        nights: offersData?.nights || nights,
        product_type: isTrain ? 'TH' : 'DP',
        total_price: selectedMealPlan?.finalTotalPrice,
        total_price_currency: localization?.currency,
        default_outbound_flight_id: outboundFlightId,
        default_inbound_flight_id: inboundFlightId,
        departure_flight_small_bag_included: defaultFlight.outbound.handBaggageIncluded ? 1 : 0,
        return_flight_small_bag_included: defaultFlight.inbound.handBaggageIncluded ? 1 : 0,
        outbound_departure_time: format(
          parse(defaultFlight.outbound.departureDate, 'dd/MM/yy#HH:mm', new Date()),
          'yyyy-MM-dd HH:mm:ss'
        ), // Format YYYY-MM-DD HH:MM:SS
        inbound_departure_time: format(
          parse(defaultFlight.inbound.departureDate, 'dd/MM/yy#HH:mm', new Date()),
          'yyyy-MM-dd HH:mm:ss'
        ), // Format YYYY-MM-DD HH:MM:SS
        default_flight_is_direct:
          defaultFlight?.outbound.segments.length === 1 && defaultFlight?.inbound.segments.length === 1 ? 1 : 0,
      });
      starTrack.changeFlightPageShown(
        createChangeFlightPageShownPayload({
          searchId: searchId || '',
          productDetailKey,
          page: changeFlightOriginPage,
        })
      );

      // In evo this section is the change-flight modal, where CHANGE_FLIGHT_PAGE_SHOWN is the relevant
      // event; CHOOSE_FLIGHT_SHOWN is sent from the evo flight page instead (useTrackEvoChooseTransportShown),
      // so it maps to a single surface as in the non-evo flow.
      if (!localization.configValuesApp.evolutionEnabled) {
        starTrack.chooseFlightShown(
          createChooseFlightShownPayload({
            searchId: searchId || '',
            productDetailKey,
            flightClickId: initialParams.flightClickId || fallbackFlightClickId.current,
            transport: offersData?.transport?.transports?.[0],
          })
        );
      }
    }
  }, [transportData?.transports?.[0]]);

  // Own effect: the package is seeded by SelectedMealPlanProvider, a parent whose effect runs after this
  // one, so wait for a selection belonging to THESE offers. A non-OK response keeps the previous
  // selection, so report no price at all — trackEventError above already covers sold out, as hdp.details
  // does. The ref only dedups that wait within one mount, so this is once per flight PER MOUNT: as the
  // evo change-flight modal it unmounts on close, so reopening re-reports — the pre-MR cadence, kept.
  useEffect(() => {
    // Opened from the evo flight page, that page owns 'hdp.chooseTransport'
    // (useTrackEvoChooseTransportShown) and emitting here too would double-report every flight change at
    // the pre-change price. Opened from the evo hotel detail page nothing else owns it, so it stays.
    if (localization.configValuesApp.evolutionEnabled && changeFlightOriginPage === 'Flight Detail') return;
    const defaultFlight = transportData?.transports?.[0];
    if (!defaultFlight || visitSentForFlightRef.current === defaultFlight.id) return;
    if (offersData?.responseCode !== 'OK' || !isMealPlanInRooms(offersData?.rooms, selectedMealPlan)) return;
    visitSentForFlightRef.current = defaultFlight.id;
    trackVisitEvent({
      t,
      pageSection: 'hdp.chooseTransport',
      mealPlan: selectedMealPlan,
      roomName: selectedRoom?.name ?? '',
      checkin: offersData?.checkin || '',
      checkout: offersData?.checkout || '',
      hotelStaticData,
      initialParams,
      promotionType: hotelPromotions?.promotionType || '',
      urlSearchParams,
      pricingId: asyncPricing?.pricingId,
      staticData,
      commonData,
      localization,
      selectedFlight: accommodationOnly ? undefined : offersData?.transport?.transports[0],
      searchInfoDetailData,
    });
  }, [transportData?.transports?.[0], selectedMealPlan, selectedRoom]);

  // Send DP_SHOWN_FLIGHTS event when user clicks "show more" and new flights are loaded
  useEffect(() => {
    const currentLength = visibleTransports.length;

    // Only send event if:
    // 1. This is not the initial load (previousLength > 0)
    // 2. New flights were added (currentLength > previousLength)
    // 3. We're on page > 1 (pagination scenario)
    if (
      previousVisibleTransportsLength.current > 0 &&
      currentLength > previousVisibleTransportsLength.current &&
      page > 1
    ) {
      sendDpShownFlightsEvent();
    }

    previousVisibleTransportsLength.current = currentLength;
  }, [visibleTransports.length, page, sendDpShownFlightsEvent]);

  return !offersData?.noAvailableRooms && !transportIsSoldOut ? (
    <ChangeTransportFilterProvider>
      <TransportPageContentSection {...rest}>
        {!isTrain && transportData && cityWithoutAirport && distanceToHotelData && (
          <DestinationWithoutAirportMessage destination={hotelStaticData?.city || 'City'} />
        )}
        {isTrain && isBelgiumTrainsSpecialFare(transportData?.transports?.[0] as TransportsType) ? (
          <BelgiumTrainsFareMessage showIcon={true} />
        ) : null}

        {isTrain && isNotHotelInBrussels(transportData?.transports?.[0] as TransportsType, destination) ? (
          <HotelNotInBrusselsMessage />
        ) : null}

        <TimeCheckoutDepartureMessage
          isTrain={isTrain}
          checkoutTo={checkoutTo}
          departureDate={transportData?.transports?.[0]?.inbound.departureDate}
        />

        {!isTrain && airportInDifferentIsland ? <AirportInDifferentIslandMessage /> : null}

        {localization.configValuesApp.evolutionEnabled ? <EvoTransportPageLayout /> : <ChangeTransportPageLayout />}

        {isFallbackTransport && transportData && !localization.configValuesApp.evolutionEnabled && (
          <TransportCard transportData={transportData} />
        )}

        {!isDesktop &&
          !transportsFetchError &&
          !isFallbackTransport &&
          !localization.configValuesApp.evolutionEnabled && <ChangeTransportFiltersSortModal isTrain={isTrain} />}
      </TransportPageContentSection>
    </ChangeTransportFilterProvider>
  ) : null;
}

const isAirportInDifferentIsland = (transports: TransportsType[], destination: string | null | undefined) => {
  if (transports.length > 0) {
    const outbounds = transports[0].outbound.segments;
    const citiesCoveredByDestinationAirport = outbounds[outbounds.length - 1]?.arrivalHub?.citiesCoveredByAirport || [];
    const inbounds = transports[0].inbound.segments;
    const citiesCoveredByOriginAirport = inbounds[0]?.departureHub?.citiesCoveredByAirport || [];
    return !(
      (citiesCoveredByDestinationAirport.length === 0 ||
        !!citiesCoveredByDestinationAirport.find((cityCovered: number) => `${cityCovered}` === destination)) &&
      (citiesCoveredByOriginAirport.length === 0 ||
        !!citiesCoveredByOriginAirport.find((cityCovered: number) => `${cityCovered}` === destination))
    );
  }
  return false;
};
